import { useState, Dispatch, SetStateAction } from 'react';
import {
  Button,
  Col,
  Form,
  Row,
  Stack,
  InputGroup,
  ToggleButtonGroup,
  ToggleButton,
} from 'react-bootstrap';
import { useSearchParams } from 'react-router-dom';
import { BiSearch } from 'react-icons/bi';
import RefreshButton from '@company/components/RefreshButton/RefreshButton';
import ThDateRangePicker from '@components/elements/ThDateRangePicker/ThDateRangePicker';
import ModalMessage from '@components/elements/ModalMessage/ModalMessage';
import { jobFilterMap, jobShiftsFilterMap } from '@utils/JobUtils';
import { ActivityDetail } from '@th-types/daily-activity.type';
import { futureStaticRanges } from '@utils/DateUtils';
import IJobDetail from '@th-types/job.detail.type';
import {
  IJobListFilters,
  IJobSearchType,
  IJobSearchScope,
  IJobSearchPeriod,
  JobListFiltersKeys,
  NewApplicantsCounterResults,
  JobListViews,
  JobShiftsQuickFilters,
  IJobShiftsCounters,
} from '@th-types/jobs.type';
import JobViewQuickFilters from '../JobViewQuickFilters';
import JobShiftsViewQuickFilters from '../JobShifts/JobShiftsViewQuickFilters';
import './style.css';

const SERVER_URL = import.meta.env.VITE_SERVER_BASE_URL;
const SHOW_JOB_SHIFTS_VIEW = import.meta.env.VITE_SHOW_JOB_SHIFTS;

interface Props {
  countHiddenJobs: number;
  countActiveJobs: number;
  jobList?: IJobDetail[];
  jobActivities?: ActivityDetail[];
  jobListFilters: IJobListFilters;
  jobShiftsCounters?: IJobShiftsCounters;
  newApplicantsCounters?: NewApplicantsCounterResults;
  setCurrentOffset: (currentOffset: number) => void;
  setJobListViewType: (jobListViewType: JobListViews) => void;
  setJobListFilters: Dispatch<SetStateAction<IJobListFilters>>;
  searchJobs: () => void;
}

enum JobQuickFilters {
  NEW_APPLICANTS = 'New Applicants',
  HIDDEN_JOBS = 'Hidden Jobs',
}

export default function JobListManagerHeader({
  jobList,
  jobActivities,
  jobListFilters,
  countActiveJobs,
  countHiddenJobs,
  newApplicantsCounters,
  jobShiftsCounters,
  searchJobs,
  setCurrentOffset,
  setJobListFilters,
  setJobListViewType,
}: Props) {
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [pickedDateRange, setPickedDateRange] = useState<{
    startDate: Date | undefined;
    endDate: Date | undefined;
  }>({
    startDate: jobListFilters?.dateRange?.startDate ?? undefined,
    endDate: jobListFilters?.dateRange?.endDate ?? undefined,
  });
  const [searchValue, setSearchValue] = useState(
    jobListFilters.searchFilter || ''
  );

  const [searchParams, setSearchParams] = useSearchParams();

  const handleActiveButton = () => {
    setCurrentOffset(0);
    const jobSearchType =
      jobListFilters.scope === IJobSearchScope.MEMBER
        ? IJobSearchType.ACTIVE_MEMBER
        : IJobSearchType.ACTIVE_COMPANY;

    setJobListFilters((previousFilters) => ({
      ...previousFilters,
      jobSearchType,
      period: IJobSearchPeriod.ACTIVE,
    }));
  };

  const handleCompletedButton = () => {
    setCurrentOffset(0);
    const jobSearchType =
      jobListFilters.scope === IJobSearchScope.MEMBER
        ? IJobSearchType.PAST_MEMBER
        : IJobSearchType.PAST_COMPANY;

    setJobListFilters((previousFilters) => ({
      ...previousFilters,
      jobSearchType,
      period: IJobSearchPeriod.PAST,
      newApplicantsOnly: false,
    }));
  };

  const handleTextSearch = (value: string) => {
    setSearchValue(value);
    setJobListFilters((previousFilters) => ({
      ...previousFilters,
      searchFilter: value,
    }));
  };

  const fetchCompanyJobs = async () => {
    setCurrentOffset(0);
    const jobSearchType =
      jobListFilters.period === IJobSearchPeriod.ACTIVE ||
      jobListFilters.viewType === JobListViews.SHIFTS
        ? IJobSearchType.ACTIVE_COMPANY
        : IJobSearchType.PAST_COMPANY;
    setJobListFilters((previousFilters) => ({
      ...previousFilters,
      scope: IJobSearchScope.COMPANY,
      jobSearchType,
    }));
  };

  const fetchMemberJobs = async () => {
    setCurrentOffset(0);
    const jobSearchType =
      jobListFilters.period === IJobSearchPeriod.ACTIVE ||
      jobListFilters.viewType === JobListViews.SHIFTS
        ? IJobSearchType.ACTIVE_MEMBER
        : IJobSearchType.PAST_MEMBER;

    setJobListFilters((previousFilters) => ({
      ...previousFilters,
      scope: IJobSearchScope.MEMBER,
      jobSearchType,
    }));
  };

  const handleDateRangeChange = (
    dateRange: Array<{ startDate: Date; endDate: Date }>
  ) => {
    const { startDate, endDate } = dateRange[0];

    setPickedDateRange({
      startDate,
      endDate,
    });

    setJobListFilters((previousFilters) => ({
      ...previousFilters,
      dateRange: {
        startDate,
        endDate,
      },
    }));
    searchParams.set(
      JobListFiltersKeys.DATE_FROM,
      startDate?.toDateString() ?? ''
    );
    searchParams.set(JobListFiltersKeys.DATE_TO, endDate?.toDateString() ?? '');
    setSearchParams(searchParams);
  };

  const clearFilters = () => {
    setJobListFilters((previousFilters) => ({
      ...previousFilters,
      searchFilter: '',
      dateRange: {
        startDate: undefined,
        endDate: undefined,
      },
    }));
    setPickedDateRange({ startDate: undefined, endDate: undefined });
    setSearchValue('');
    searchParams.set(JobListFiltersKeys.DATE_FROM, '');
    searchParams.set(JobListFiltersKeys.DATE_TO, '');
    searchParams.set(JobListFiltersKeys.SEARCH_TEXT, '');
    setSearchParams(searchParams);
  };

  const executeJobsViewBadgeAction = (quickFilterName: JobQuickFilters) => {
    setCurrentOffset(0);
    const filterKey = jobFilterMap[quickFilterName];
    if (filterKey) {
      setJobListFilters((previousFilters) => ({
        ...previousFilters,
        [filterKey]: !previousFilters[filterKey],
      }));
    }
  };

  const executeJobShiftsViewBadgeAction = (
    quickFilterName: JobShiftsQuickFilters
  ) => {
    setCurrentOffset(0);

    const filterKey = jobShiftsFilterMap[quickFilterName];
    const newFilters = { ...jobListFilters };
    if (filterKey) {
      const filterValue = jobListFilters[filterKey];
      if (filterValue === true) {
        // Set all job shifts filters to false
        Object.values(jobShiftsFilterMap).forEach((key) => {
          (newFilters as any)[key] = false;
        });
      } else {
        // Set clicked job shifts filter to true and all other job shifts filters to false
        Object.values(jobShiftsFilterMap).forEach((key) => {
          (newFilters as any)[key] = key === filterKey;
        });
      }
    }
    setJobListFilters(newFilters);
  };

  const changeViewType = (viewType: JobListViews) => {
    if (SHOW_JOB_SHIFTS_VIEW === 'true') {
      setJobListViewType(viewType);
    } else {
      setShowMessageModal(true);
    }
  };

  return (
    <div className="job-list-header-container">
      <Row className="m-3 job-list-header">
        <Col md={4}>
          <Stack direction="horizontal" gap={3} className="h-100">
            <ToggleButtonGroup
              type="radio"
              name="options"
              value={1}
              className="border rounded"
            >
              <ToggleButton
                value={JobListViews.JOBS}
                id={JobListViews.JOBS}
                onClick={() => changeViewType(JobListViews.JOBS)}
                variant={`${jobListFilters.viewType === JobListViews.JOBS ? 'primary' : 'outline-dark'}`}
                className="border-0"
              >
                <strong>{JobListViews.JOBS}</strong>
              </ToggleButton>
              <ToggleButton
                value={JobListViews.SHIFTS}
                id={JobListViews.SHIFTS}
                onClick={() => changeViewType(JobListViews.SHIFTS)}
                variant={`${jobListFilters.viewType === JobListViews.SHIFTS ? 'primary' : 'outline-dark'}`}
                className="shift-btn-tab border-0"
              >
                <strong>{JobListViews.SHIFTS}</strong>
              </ToggleButton>
            </ToggleButtonGroup>

            <InputGroup>
              <InputGroup.Text className="search-icon">
                <BiSearch size={20} className="mt-1" />
              </InputGroup.Text>
              <Form.Control
                placeholder="By event name, brand or city"
                className="form-control-header"
                value={searchValue}
                onChange={(e) => handleTextSearch(e.target.value)}
                onKeyUp={(event) => {
                  if (event.key === 'Enter') {
                    searchJobs();
                  }
                }}
              />
            </InputGroup>
          </Stack>
        </Col>
        <Col md={2}>
          <ThDateRangePicker
            start={pickedDateRange.startDate}
            end={pickedDateRange.endDate}
            setStart={() => undefined}
            setEnd={() => undefined}
            addMaxDate={false}
            onChange={handleDateRangeChange}
            customStaticRange={futureStaticRanges}
            useGrayBackground
          />
        </Col>
        <Col md={2}>
          <Stack direction="horizontal" gap={2} className="h-100">
            <Button
              variant="primary"
              onClick={searchJobs}
              className="btn-font-size"
            >
              <strong>SEARCH</strong>
            </Button>
            <Button
              variant="light"
              onClick={clearFilters}
              className="btn-font-size"
            >
              <strong>CLEAR</strong>
            </Button>
          </Stack>
        </Col>
        <Col md={4} className="d-flex">
          <Col md={3} className="d-flex justify-content-center">
            <RefreshButton isSmall refreshFunction={searchJobs} />
          </Col>
          <Col md={9} className="d-flex justify-content-end">
            <a
              href={`${SERVER_URL}/job/create`}
              target="_blank"
              rel="noreferrer"
            >
              <Button
                variant="primary"
                className="h-100 me-2 btn-font-size fw-bold"
              >
                CREATE JOB
              </Button>
            </a>

            <Button
              variant="light"
              className="btn-font-size scope-btn fw-bold"
              onClick={() => {
                if (jobListFilters.scope === IJobSearchScope.MEMBER) {
                  fetchCompanyJobs();
                  return;
                }
                fetchMemberJobs();
              }}
            >
              {jobListFilters.scope === IJobSearchScope.COMPANY
                ? 'MY JOBS'
                : 'COMPANY JOBS'}
            </Button>
          </Col>
        </Col>
      </Row>
      <Row className="mx-3">
        <Col
          className={`d-flex gap-2 ${jobListFilters.viewType === JobListViews.JOBS ? 'ms-1' : ''}`}
          md={`${jobListFilters.viewType === JobListViews.JOBS ? 6 : 8}`}
        >
          {jobListFilters.viewType === JobListViews.JOBS && (
            <>
              <Button
                variant="link"
                size="sm"
                className={`text-capitalize quick-filter-btn-link p-0 ${jobListFilters.period === IJobSearchPeriod.PAST ? 'quick-filter-btn-deselected' : ''}`}
                onClick={() => handleActiveButton()}
              >
                {jobListFilters.period === IJobSearchPeriod.ACTIVE ? (
                  <strong>{`Active (${countActiveJobs})`}</strong>
                ) : (
                  <div>{`Active (${countActiveJobs})`}</div>
                )}
              </Button>
              <Button
                variant="link"
                size="sm"
                className={`pe-1 text-capitalize quick-filter-btn-link ${jobListFilters.period === IJobSearchPeriod.ACTIVE ? 'quick-filter-btn-deselected' : ''}`}
                onClick={handleCompletedButton}
              >
                {jobListFilters.period === IJobSearchPeriod.PAST ? (
                  <strong>Past</strong>
                ) : (
                  'Past'
                )}
              </Button>

              <div className="vl" />
            </>
          )}

          {jobListFilters.viewType === JobListViews.JOBS && (
            <JobViewQuickFilters
              jobListFilters={jobListFilters}
              countHiddenJobs={countHiddenJobs}
              executeJobsViewBadgeAction={executeJobsViewBadgeAction}
              newApplicantsCounters={newApplicantsCounters}
            />
          )}
          {jobList &&
            jobActivities &&
            jobListFilters.viewType === JobListViews.SHIFTS && (
              <JobShiftsViewQuickFilters
                jobShiftsCounters={jobShiftsCounters}
                jobListFilters={jobListFilters}
                executeJobShiftsViewBadgeAction={
                  executeJobShiftsViewBadgeAction
                }
              />
            )}
        </Col>
      </Row>
      {showMessageModal && (
        <ModalMessage
          message="This is exciting. We're coding your ability to track all your shifts in a 10,000 foot view, right from a single page"
          setShowModal={setShowMessageModal}
        />
      )}
    </div>
  );
}
